import { useCallback, useMemo, useState } from 'react';

/**
 * Either an explicit id set, or "every asset matching the current query" minus the ids
 * unticked afterwards, so "Select all N entries" works without loading every page (D-037).
 */
type DamSelection =
    | { mode: 'ids'; ids: ReadonlySet<string> }
    | { mode: 'allMatching'; excludedIds: ReadonlySet<string> };

type DamSelectAllState = 'none' | 'some' | 'all';

const EMPTY_SELECTION: DamSelection = { mode: 'ids', ids: new Set() };

const withId = (set: ReadonlySet<string>, id: string, include: boolean) => {
    const next = new Set(set);
    if (include) {
        next.add(id);
    } else {
        next.delete(id);
    }
    return next;
};

function useDamSelection(totalFound: number) {
    const [selection, setSelection] = useState<DamSelection>(EMPTY_SELECTION);

    const isSelected = useCallback((id: string) => (
        selection.mode === 'ids' ? selection.ids.has(id) : !selection.excludedIds.has(id)
    ), [selection]);

    /** Selects or unselects the given ids, e.g. one card or every row of the page. */
    const setSelected = useCallback((ids: string[], selected: boolean) => {
        setSelection((previous) => {
            if (previous.mode === 'ids') {
                return { mode: 'ids', ids: ids.reduce((set, id) => withId(set, id, selected), previous.ids) };
            }
            return { mode: 'allMatching', excludedIds: ids.reduce((set, id) => withId(set, id, !selected), previous.excludedIds) };
        });
    }, []);

    const selectedCount = selection.mode === 'ids'
        ? selection.ids.size
        : Math.max(0, totalFound - selection.excludedIds.size);

    let selectAllState: DamSelectAllState = 'some';
    if (selectedCount === 0) {
        selectAllState = 'none';
    } else if (selection.mode === 'allMatching' && selection.excludedIds.size === 0) {
        selectAllState = 'all';
    }

    const selectAll = useCallback((selected: boolean) => {
        setSelection(selected ? { mode: 'allMatching', excludedIds: new Set() } : EMPTY_SELECTION);
    }, []);

    const clear = useCallback(() => setSelection(EMPTY_SELECTION), []);

    return useMemo(() => ({
        selection, isSelected, setSelected, selectAll, clear, selectedCount, selectAllState,
    }), [selection, isSelected, setSelected, selectAll, clear, selectedCount, selectAllState]);
}

export { useDamSelection };
export type { DamSelectAllState, DamSelection };
